import React, { useEffect, useMemo, useState } from "react";
import { Alert, Badge, Button, Card, Col, ProgressBar, Row, Spinner } from "react-bootstrap";
import * as FileSaver from "file-saver";
import EmpleadosService from "../services/empleados.service";
import CajasService from "../services/cajas.service";
import "./dashboardCajas.css";

const ExcelJS = require("exceljs");

const nf = new Intl.NumberFormat("es-AR", {
  style: "currency",
  currency: "ARS",
  minimumFractionDigits: 2,
  maximumFractionDigits: 2,
});

const safeNumber = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

const DashboardCajas = () => {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [cajasAbiertas, setCajasAbiertas] = useState([]);

  const fetchCajas = async () => {
    setLoading(true);
    setError("");

    try {
      const responseEmpleados = await EmpleadosService.getAllEmpleados();
      const empleados = responseEmpleados.data || [];

      const requests = empleados.map((empleado) =>
        CajasService.searchCaja(empleado.CodigoVendedor)
      );

      const results = await Promise.allSettled(requests);

      const abiertas = results
        .map((result, index) => {
          if (result.status !== "fulfilled") {
            return null;
          }

          const caja = result.value?.data;
          if (!caja || !caja.montos) {
            return null;
          }

          const empleado = empleados[index];

          return {
            idCaja: caja.idCaja,
            codigoVendedor: empleado?.CodigoVendedor,
            nombreEmpleado: caja.user?.empleado?.Nombre || empleado?.Nombre || "-",
            apellidoEmpleado: caja.user?.empleado?.Apellido || empleado?.Apellido || "",
            montos: {
              efectivo: safeNumber(caja.montos.montoEfectivo),
              transferencia: safeNumber(caja.montos.montoTransferencia),
              cheque: safeNumber(caja.montos.montoCheque),
              tarjeta: safeNumber(caja.montos.montoTarjeta),
              total: safeNumber(caja.montos.montoTotal),
            },
          };
        })
        .filter(Boolean)
        .sort((a, b) => b.montos.total - a.montos.total);

      setCajasAbiertas(abiertas);
    } catch (err) {
      setError(
        err?.response?.data?.message ||
          "No pudimos cargar el dashboard de cajas."
      );
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    let mounted = true;

    if (mounted) {
      fetchCajas();
    }

    return () => {
      mounted = false;
    };
  }, []);

  const resumen = useMemo(() => {
    return cajasAbiertas.reduce(
      (acc, caja) => {
        acc.efectivo += caja.montos.efectivo;
        acc.transferencia += caja.montos.transferencia;
        acc.cheque += caja.montos.cheque;
        acc.tarjeta += caja.montos.tarjeta;
        acc.total += caja.montos.total;
        return acc;
      },
      {
        efectivo: 0,
        transferencia: 0,
        cheque: 0,
        tarjeta: 0,
        total: 0,
      }
    );
  }, [cajasAbiertas]);

  const tipos = [
    {
      key: "efectivo",
      label: "Efectivo",
      icon: "EF",
      color: "#1d7a4f",
      bg: "linear-gradient(135deg, #f3fbf6 0%, #e8f6ee 100%)",
      value: resumen.efectivo,
    },
    {
      key: "transferencia",
      label: "Transferencia",
      icon: "TR",
      color: "#1f4d8f",
      bg: "linear-gradient(135deg, #f1f6fd 0%, #e4eefb 100%)",
      value: resumen.transferencia,
    },
    {
      key: "cheque",
      label: "Cheque",
      icon: "CH",
      color: "#945d1f",
      bg: "linear-gradient(135deg, #fdf8f1 0%, #faefde 100%)",
      value: resumen.cheque,
    },
    {
      key: "tarjeta",
      label: "Tarjeta",
      icon: "TJ",
      color: "#6c3c8a",
      bg: "linear-gradient(135deg, #fbf6ff 0%, #f3e8ff 100%)",
      value: resumen.tarjeta,
    },
  ];

  const exportarExcel = async () => {
    const header = [
      "Vendedor",
      "Empleado",
      "Efectivo",
      "Transferencia",
      "Cheque",
      "Tarjeta",
      "Total",
    ];

    const detalleRows = cajasAbiertas.map((caja) => [
      caja.codigoVendedor ?? "",
      `${caja.nombreEmpleado} ${caja.apellidoEmpleado}`.trim(),
      caja.montos.efectivo,
      caja.montos.transferencia,
      caja.montos.cheque,
      caja.montos.tarjeta,
      caja.montos.total,
    ]);

    const resumenRows = [
      ["", "Saldo general", resumen.total],
      ["", "Efectivo", resumen.efectivo],
      ["", "Transferencia", resumen.transferencia],
      ["", "Cheque", resumen.cheque],
      ["", "Tarjeta", resumen.tarjeta],
    ];

    const wb = new ExcelJS.Workbook();
    const ws = wb.addWorksheet("Dashboard");

    const fechaActual = new Date().toLocaleDateString("es-AR");
    ws.getCell("A1").value = `Cajas Abiertas Día ${fechaActual}`;

    ws.addRow(header);

    detalleRows.forEach((row) => {
      ws.addRow(row);
    });

    ws.addRow([]);
    const resumenStartRow = ws.lastRow.number + 1;
    resumenRows.forEach((row) => {
      ws.addRow(row);
    });

    ws.eachRow((row) => {
      row.eachCell((cell) => {
        cell.font = {
          ...cell.font,
          name: "Arial",
          size: 16,
          bold: false,
        };
      });
    });

    const tituloCell = ws.getCell("A1");
    tituloCell.font = { ...tituloCell.font, bold: true, size: 18, name: "Arial" };

    const headerRow = ws.getRow(2);
    headerRow.eachCell((cell) => {
      cell.font = { ...cell.font, bold: true, size: 16, name: "Arial" };
    });

    for (let rowIndex = 3; rowIndex <= 2 + detalleRows.length; rowIndex += 1) {
      [3, 4, 5, 6, 7].forEach((colIndex) => {
        ws.getCell(rowIndex, colIndex).numFmt = "#,##0.00";
      });
    }

    for (
      let rowIndex = resumenStartRow;
      rowIndex < resumenStartRow + resumenRows.length;
      rowIndex += 1
    ) {
      const labelCell = ws.getCell(rowIndex, 2);
      const valueCell = ws.getCell(rowIndex, 3);

      labelCell.font = { ...labelCell.font, bold: true, size: 16, name: "Arial" };
      valueCell.font = { ...valueCell.font, bold: true, size: 16, name: "Arial" };
      valueCell.numFmt = "#,##0.00";
    }

    for (let colIndex = 1; colIndex <= ws.columnCount; colIndex += 1) {
      const column = ws.getColumn(colIndex);
      let maxLength = 0;

      column.eachCell({ includeEmpty: true }, (cell) => {
        let text = "";

        if (cell.value !== null && cell.value !== undefined) {
          if (typeof cell.value === "object") {
            if (cell.value.richText) {
              text = cell.value.richText.map((item) => item.text).join("");
            } else if (cell.value.text) {
              text = cell.value.text;
            } else if (cell.value.result !== undefined) {
              text = String(cell.value.result);
            } else {
              text = String(cell.value);
            }
          } else {
            text = String(cell.value);
          }
        }

        if (typeof cell.value === "number") {
          text = text + "  ";
        }

        maxLength = Math.max(maxLength, text.length);
      });

      const extra = colIndex === 2 ? 18 : 2;
      const maxWidth = colIndex === 2 ? 520 : 240;
      column.width = Math.min(maxWidth, Math.max(24, maxLength + extra));
    }

    const buffer = await wb.xlsx.writeBuffer();
    const fileType =
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet;charset=UTF-8";
    const data = new Blob([buffer], { type: fileType });
    const stamp = new Date().toISOString().slice(0, 19).replace(/[:T]/g, "-");

    FileSaver.saveAs(data, `Dashboard-Cajas-${stamp}.xlsx`);
  };

  if (loading) {
    return (
      <Row className="justify-content-center py-4">
        <Spinner animation="border" role="status" />
      </Row>
    );
  }

  if (error) {
    return <Alert variant="danger">{error}</Alert>;
  }

  return (
    <div className="dc-root">
      <div className="dc-hero dc-hero-standalone">
        <Row className="align-items-center g-1">
          <Col>
            <div className="dc-hero-title">Dashboard de Cajas</div>
            <div className="dc-hero-subtitle">
              Saldo consolidado y detalle por medio de pago en tiempo real
            </div>
          </Col>
          <Col xs={12} lg="auto">
            <div className="dc-hero-actions">
              <Badge bg="light" text="dark" pill className="dc-badge">
                {cajasAbiertas.length} abiertas
              </Badge>
              <Button
                variant="light"
                className="dc-refresh-btn"
                onClick={exportarExcel}
                disabled={cajasAbiertas.length === 0}
              >
                Exportar Excel
              </Button>
              <Button variant="light" className="dc-refresh-btn" onClick={fetchCajas}>
                Actualizar
              </Button>
            </div>
          </Col>
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 mb-1">
          <Col xs={12}>
            <Card className="dc-card dc-kpi-card">
              <Card.Body>
                <div className="dc-kpi-label">SALDO GENERAL</div>
                <div className="dc-kpi-value">
                  {nf.format(resumen.total)}
                </div>
                <div className="dc-kpi-note">
                  Acumulado de todas las cajas abiertas
                </div>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 mb-1">
          {tipos.map((tipo) => (
            <Col key={tipo.key} xs={12} sm={6} lg={3}>
              <Card className="dc-card" style={{ background: tipo.bg }}>
                <Card.Body>
                  <div className="dc-type-head mb-2">
                    <span className="dc-chip" style={{ color: tipo.color, background: "#ffffffaa" }}>
                      <span>{tipo.icon}</span>
                      <span className="dc-chip-label">{tipo.label}</span>
                    </span>
                    <span className="dc-type-percent">
                      {resumen.total > 0
                        ? `${((tipo.value / resumen.total) * 100).toFixed(1)}%`
                        : "0.0%"}
                    </span>
                  </div>
                  <div className="dc-type-value">
                    {nf.format(tipo.value)}
                  </div>
                  <ProgressBar
                    now={resumen.total > 0 ? (tipo.value / resumen.total) * 100 : 0}
                    className="dc-progress"
                    variant="warning"
                  />
                </Card.Body>
              </Card>
            </Col>
          ))}
        </Row>
      </div>

      <div className="dc-block">
        <Row className="g-1 dc-cajas-row">
          {cajasAbiertas.length === 0 ? (
            <Col>
              <Alert variant="warning" className="mb-0">
                No hay cajas abiertas en este momento.
              </Alert>
            </Col>
          ) : (
            cajasAbiertas.map((caja) => (
              <Col key={`${caja.idCaja}-${caja.codigoVendedor}`} xs={12} md={6}>
                <Card className="dc-card dc-caja-card">
                  <Card.Body>
                    <div className="dc-caja-head">
                      <div className="dc-caja-persona">
                        <Card.Title className="dc-caja-title">
                          Caja #{caja.idCaja}
                        </Card.Title>
                        <Card.Subtitle className="text-muted dc-caja-subtitle">
                          {caja.nombreEmpleado} {caja.apellidoEmpleado}
                        </Card.Subtitle>
                      </div>
                      <Badge
                        bg="light"
                        text="dark"
                        className="dc-vendedor-badge"
                      >
                        Vend. {caja.codigoVendedor}
                      </Badge>
                    </div>

                    <div className="dc-metrics-grid">
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Efectivo</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.efectivo)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Transferencia</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.transferencia)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Cheque</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.cheque)}</div>
                      </div>
                      <div className="dc-metric-box">
                        <div className="dc-metric-label">Tarjeta</div>
                        <div className="dc-metric-value">{nf.format(caja.montos.tarjeta)}</div>
                      </div>
                    </div>

                    <div className="dc-total-box mt-1">
                      <div className="dc-total-label">Total de caja</div>
                      <div className="dc-total-value">
                        {nf.format(caja.montos.total)}
                      </div>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))
          )}
        </Row>
      </div>
    </div>
  );
};

export default DashboardCajas;
